import React from "react";
import styled from "styled-components";
import Button from "../../../../library/Button";
import { tokens } from "../../../../../public/tokens";
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from "../../../../library/breakpoints";
import VersionCard from "./VersionCard";

/*
 * Now that the Versions page itself is full-width (no page-level max-width), the card row
 * uses a responsive CSS grid instead of fixed-430px flex-wrap tracks: 3 equal columns fill
 * the entire available row width at desktop (each `VersionCard` is `width: 100%` of its own
 * column, not a fixed px), dropping to 2 columns at the shared tablet breakpoint and 1 at
 * the mobile one, so cards never leave a gutter on wide screens or get squeezed on narrow ones.
 */
const StyledGrid = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    alignItems: "start",
    gap: tokens.globals["spacing-4"],
    width: "100%",
    boxSizing: "border-box",

    [`@media (max-width: ${TABLET_BREAKPOINT})`]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
    },
});

const StyledMessage = styled.div({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    padding: `120px 0`,
});

const StyledMessageTitle = styled.p(({ $isError }) => ({
    margin: 0,
    color: $isError ? tokens.colors.states.alert["500"] : tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["xl-18px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
}));

const StyledMessageBody = styled.p({
    margin: 0,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
});

/*
 * Extends the shared `Button` (theme-aware background) rather than a bespoke styled button.
 * No marginTop - the parent StyledMessage's own 8px gap already provides the spacing above it.
 */
const StyledRetryButton = styled(Button)({
    height: "35px",
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
});

/**
 * The card area below the active-version summary/header bar: a loading
 * spinner+message, an error message with Retry, an empty-list message, or
 * the grid of `VersionCard`s. Mirrors (not duplicates) the header bar's own
 * state, per the Figma "State: Loading/Empty/Load error" frames, which both
 * areas reflect independently.
 */
function VersionCardsGrid({ status, versions = [], programmeId, notesAudit, onRetry, onDelete }) {
    if (status === "loading") {
        return (
            <StyledMessage>
                <i className="fal fa-spinner fa-spin" aria-hidden="true" />
                <StyledMessageBody>Loading versions…</StyledMessageBody>
            </StyledMessage>
        );
    }

    if (status === "error") {
        return (
            <StyledMessage>
                <StyledMessageTitle $isError>Versions couldn’t be loaded</StyledMessageTitle>
                <StyledMessageBody>Something went wrong while loading versions. Retry to try again.</StyledMessageBody>
                <StyledRetryButton type="button" variant="primary" size="lg" onClick={onRetry}>Retry</StyledRetryButton>
            </StyledMessage>
        );
    }

    if (!versions.length) {
        return (
            <StyledMessage>
                <StyledMessageTitle>No versions yet</StyledMessageTitle>
                <StyledMessageBody>
                    No versions have been created for this programme. Use Create version to add the first one.
                </StyledMessageBody>
            </StyledMessage>
        );
    }

    return (
        <StyledGrid>
            {versions.map((version) => {
                const noteAudit = notesAudit?.get(String(version.id));

                return (
                    <VersionCard
                        key={version.id}
                        id={version.id}
                        programmeId={programmeId}
                        name={version.name}
                        isActive={Number(version.id) === Number(version.version_id)}
                        isLocked={Boolean(version.lock_version_settings)}
                        coordinator={version.coordinator}
                        totals={{
                            programmes: version.programmes,
                            modules: version.modules,
                            students: version.students,
                            graduates: version.graduates,
                        }}
                        unassignedModulesCount={version.unassignedModules ?? 0}
                        notes={version.notes}
                        noteAuthor={noteAudit?.author}
                        noteUpdatedAt={noteAudit?.updatedAt}
                        createdAt={version.created_at}
                        modifiedAt={version.updated_at}
                        onDelete={onDelete}
                    />
                );
            })}
        </StyledGrid>
    );
}

export default VersionCardsGrid;
